import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '@src/environments/environment';
import { ResponseData } from '@src/app/shared/components/table/table';
import { Banner } from '@src/app/core/models/cms/banner.model';
import { PaginationParams } from '@src/app/core/services/base.service';

/** Body para PUT /internal-api/banner */
export interface UpdateBannerDto {
  id: number;
  title: string;
  bannerType: number;
  itemLocation: number;
  isActive: boolean;
  width: number;
  height: number;
}

/** Body para POST /internal-api/banner */
export interface CreateBannerDto {
  title: string;
  bannerType: number;
  itemLocation: number;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class BannerService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/banner`;

  /**
   * GET /internal-api/banner
   * Retorno da API: array de banners.
   * Normalizado para { total, items } para o app-table.
   */
  get(params?: PaginationParams & { title?: string }): Observable<ResponseData<Banner>> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.title != null && params.title !== '') {
        httpParams = httpParams.set('title', params.title);
      }
      if (params.sortColumn != null) {
        httpParams = httpParams.set('sortColumn', params.sortColumn);
      }
      if (params.sortOrder != null) {
        httpParams = httpParams.set('sortOrder', params.sortOrder);
      }
      if (params.page != null) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.pageSize != null) {
        httpParams = httpParams.set('pageSize', params.pageSize.toString());
      }
    }
    return this.http
      .get<Banner[]>(this.apiUrl, { params: httpParams })
      .pipe(
        map((response) =>
          Array.isArray(response)
            ? { total: response.length, items: response }
            : { total: 0, items: [] }
        )
      );
  }

  /** GET /internal-api/banner/{id} */
  getById(id: number): Observable<Banner> {
    return this.http.get<Banner>(`${this.apiUrl}/${id}`);
  }

  /** POST /internal-api/banner */
  create(body: CreateBannerDto): Observable<Banner> {
    return this.http.post<Banner>(this.apiUrl, body);
  }

  /** GET /internal-api/banner/image/{imageId} - retorna { base64, contentType } */
  getImage(imageId: number): Observable<{ base64: string; contentType: string }> {
    return this.http.get<{ base64: string; contentType: string }>(`${this.apiUrl}/image/${imageId}`);
  }

  /** POST /internal-api/banner/upload-image - envia arquivo; retorno: { fileId, url } */
  uploadImage(file: File): Observable<{ fileId: number; url: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<{ fileId: number; url: string }>(`${this.apiUrl}/upload-image`, formData);
  }

  /** PUT /internal-api/banner */
  update(body: UpdateBannerDto): Observable<Banner> {
    return this.http.put<Banner>(this.apiUrl, body);
  }

  /** DELETE /internal-api/banner/{id} */
  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }
}
